import { InputHTMLAttributes, SelectHTMLAttributes, TextareaHTMLAttributes, ReactNode } from 'react';

interface BaseProps {
  label: string;
  error?: string;
  hint?: string;
}

interface InputFieldProps extends BaseProps, InputHTMLAttributes<HTMLInputElement> {
  as?: 'input';
}

interface SelectFieldProps extends BaseProps, SelectHTMLAttributes<HTMLSelectElement> {
  as: 'select';
  children: ReactNode;
}

interface TextareaFieldProps extends BaseProps, TextareaHTMLAttributes<HTMLTextAreaElement> {
  as: 'textarea';
}

type FormFieldProps = InputFieldProps | SelectFieldProps | TextareaFieldProps;

export default function FormField(props: FormFieldProps) {
  const { label, error, hint, as = 'input', ...rest } = props;
  const baseClass =
    'w-full rounded-lg border bg-white px-3 py-2 text-sm text-text-main placeholder:text-text-faint transition-all focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary';
  const borderClass = error ? 'border-red-300' : 'border-border-stroke';

  return (
    <div>
      <label className="mb-1.5 block text-xs font-bold text-text-muted">{label}</label>
      {as === 'select' ? (
        <select className={`${baseClass} ${borderClass}`} {...(rest as SelectHTMLAttributes<HTMLSelectElement>)}>
          {(props as SelectFieldProps).children}
        </select>
      ) : as === 'textarea' ? (
        <textarea
          className={`${baseClass} ${borderClass}`}
          rows={3}
          {...(rest as TextareaHTMLAttributes<HTMLTextAreaElement>)}
        />
      ) : (
        <input className={`${baseClass} ${borderClass}`} {...(rest as InputHTMLAttributes<HTMLInputElement>)} />
      )}
      {hint && !error && <p className="mt-1 text-[10px] text-text-faint">{hint}</p>}
      {error && <p className="mt-1 text-[10px] font-bold text-red-500">{error}</p>}
    </div>
  );
}
